import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { describeProviderResult, useAuth } from "../../contexts/AuthContext";
import { userService } from "../../services/userService";
import { usernameService } from "../../services/usernameService";
import { trainingTypeService } from "../../services/trainingTypeService";
import { analyticsService } from "../../services/analyticsService";
import { trackCompleteRegistration, trackInitiateCheckout } from "../../metaSignup";
import {
  OnboardingRegistrationInput,
  completeOnboarding,
  createPlanCheckoutUrl,
  markOnboardingComplete,
  saveOnboardingProfile,
  validateAmbassadorCode,
} from "../../services/onboardingService";
import { getAuthErrorMessage } from "../../utils/firebaseAuthErrors";
import { safeGetItem } from "../../utils/localStorageUtils";
import FullScreenLoader from "../common/loadingPWA";
import {
  ONBOARDING_STEPS,
  OnboardingStep,
  saveDraftNow,
  useOnboardingWizard,
} from "../../hooks/useOnboardingWizard";
import { emailError, passwordError } from "./onboardingValidation";
import { AMBASSADOR_COPY, COMPETITION_LEVELS, CompetitionLevel, FINISH_STATUS } from "./onboardingCopy";
import OnboardingFinishLoading from "./OnboardingFinishLoading";
import WelcomeStep from "./steps/WelcomeStep";
import CreateAccountStep from "./steps/CreateAccountStep";
import EmailStep from "./steps/EmailStep";
import AmbassadorCodeStep from "./steps/AmbassadorCodeStep";
import CompetitionLevelStep from "./steps/CompetitionLevelStep";
import SportsStep from "./steps/SportsStep";
import PersonalInfoStep from "./steps/PersonalInfoStep";
import GoalStep from "./steps/GoalStep";
import DifferenceStep from "./steps/DifferenceStep";
import PlanStep, { PlanChoice } from "./steps/PlanStep";

const stepIndex = (step: OnboardingStep) => ONBOARDING_STEPS.indexOf(step);

const isPopupCancelled = (error: unknown) => {
  const code = (error as { code?: string } | undefined)?.code ?? "";
  return code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request" || code === "auth/user-cancelled";
};


/**
 * State-driven onboarding at /onboarding. Mirrors iOS `OnboardingFlowView` +
 * `OnboardingController`: the Auth account is created mid-flow and the profile
 * is written when a plan is chosen. Paid plans finish onboarding when Stripe
 * Checkout returns here. The wearable, reminder and first wellness log steps
 * run in the native app (`nativeSetupPending`), so the flow ends at /download.
 */
const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    userData,
    authState,
    initializing,
    pendingTwoFactor,
    createAccountWithEmail,
    continueWithGoogle,
    continueWithApple,
    refreshUserData,
  } = useAuth();

  const checkUsername = useCallback(
    async (username: string) => {
      if (userData?.username && userData.username.toLowerCase() === username.toLowerCase()) return true;
      const result = await usernameService.checkUsernameAvailability(username);
      if (!result.valid) throw new Error(result.message);
      if (!result.available && result.message.startsWith("Unable")) throw new Error(result.message);
      return result.available;
    },
    [userData?.username],
  );

  const { state, dispatch, progress, canContinue, setUsername, goBack, continueTapped, clearDraft } =
    useOnboardingWizard({ checkUsername });

  const [planError, setPlanError] = useState<string | null>(null);
  const [ambassadorChecking, setAmbassadorChecking] = useState(false);
  const [ambassadorError, setAmbassadorError] = useState<string | null>(null);
  const leavingRef = useRef(false);
  const autoValidated = useRef(false);

  const exitToDownload = useCallback(
    (path: string) => {
      leavingRef.current = true;
      clearDraft();
      navigate(path, { replace: true });
    },
    [clearDraft, navigate],
  );

  // Drafts saved before the wellness log moved to the app can resume past the plan.
  const pastPlan = state.step === "day1" || state.step === "howPeakHelps";
  useEffect(() => {
    if (pastPlan && !leavingRef.current) {
      exitToDownload(state.planChoice === "paid" ? "/download?subscription_success=true" : "/download?welcome=1");
    }
  }, [pastPlan, state.planChoice, exitToDownload]);
  const checkoutHandled = useRef(false);

  const params = new URLSearchParams(location.search);
  const checkoutReturn =
    params.get("subscription_success") === "true"
      ? "success"
      : params.get("subscription_canceled") === "true"
        ? "canceled"
        : null;

  // Ambassador links (signup.peak.app/?code=…, /onboarding?ambassador=…)
  // prefill the code. App stores ?code= as localStorage.campaignCode.
  const linkCodeApplied = useRef(false);
  useEffect(() => {
    if (linkCodeApplied.current) return;
    linkCodeApplied.current = true;
    const linkCode = (params.get("ambassador") || params.get("code") || safeGetItem("campaignCode") || "").trim();
    if (linkCode && !state.ambassadorCode && !state.ambassadorValidated) {
      dispatch({ type: "setField", field: "ambassadorCode", value: linkCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAmbassadorCode = useCallback(async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setAmbassadorChecking(true);
    setAmbassadorError(null);
    try {
      const result = await validateAmbassadorCode(trimmed);
      if (result.valid) {
        dispatch({ type: "ambassadorValidated", campaignCode: result.campaignCode, firstName: result.ambassadorFirstName });
      } else {
        setAmbassadorError(result.reason === "inactive" ? AMBASSADOR_COPY.inactive : AMBASSADOR_COPY.notFound);
      }
    } catch (error) {
      const raw = error instanceof Error ? error.message.replace(/^Firebase:\s*/i, "").trim() : "";
      setAmbassadorError(raw || AMBASSADOR_COPY.notFound);
    } finally {
      setAmbassadorChecking(false);
    }
  }, [dispatch]);

  // A prefilled code is checked as soon as the step opens (the callable needs auth).
  useEffect(() => {
    if (state.step !== "ambassadorCode" || !currentUser || autoValidated.current) return;
    if (!state.ambassadorCode.trim() || state.ambassadorValidated) return;
    autoValidated.current = true;
    void checkAmbassadorCode(state.ambassadorCode);
  }, [state.step, state.ambassadorCode, state.ambassadorValidated, currentUser, checkAmbassadorCode]);

  // Analytics: one event per step (state-driven, so no page_view per step).
  useEffect(() => {
    analyticsService.trackOnboarding(state.step, false);
  }, [state.step]);

  // Sports catalogue, loaded once on mount (iOS loads on flow appear).
  useEffect(() => {
    if (state.availableSports.length > 0 || state.sportsLoading) return;
    dispatch({ type: "sportsLoading" });
    trainingTypeService
      .fetchSports()
      .then((sports) => dispatch({ type: "sportsLoaded", sports }))
      .catch(() => dispatch({ type: "sportsFailed", message: "No sports available right now. Try again later." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stripe Checkout returns to /onboarding?subscription_success|canceled=true.
  useEffect(() => {
    if (!checkoutReturn || checkoutHandled.current || initializing || !currentUser) return;
    checkoutHandled.current = true;
    navigate("/onboarding", { replace: true });
    if (checkoutReturn === "canceled") {
      dispatch({ type: "checkoutCanceled" });
      return;
    }
    const uid = currentUser.uid;
    (async () => {
      dispatch({ type: "submitStart", status: FINISH_STATUS.email });
      try {
        await markOnboardingComplete(uid, { onStatus: (status) => dispatch({ type: "submitStatus", status }) });
        analyticsService.trackProfileComplete(uid);
      } catch (error) {
        // The checkout webhook also completes onboarding for this session.
        console.warn("[Onboarding] could not mark onboarding complete after checkout:", error);
      }
      analyticsService.trackOnboarding("complete", true);
      refreshUserData().catch(() => {});
      exitToDownload("/download?subscription_success=true");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutReturn, initializing, currentUser, navigate, dispatch, refreshUserData]);

  // Reconcile the draft with the live auth state.
  useEffect(() => {
    if (initializing || leavingRef.current || state.submitting) return;

    if (pendingTwoFactor) {
      leavingRef.current = true;
      navigate("/download?existing=1", { replace: true });
      return;
    }

    if (!currentUser) {
      // Auth user is gone (signed out elsewhere) but the draft thinks the
      // account exists — restart from the method picker with the answers kept.
      if (state.accountReady || state.existingProfile) {
        dispatch({
          type: "restore",
          draft: { accountReady: false, existingProfile: false, emailVerifiedByProvider: false, wellnessLogged: false, step: "createAccount" },
        });
      }
      return;
    }

    // A completed profile mid-checkout-return is finished by the effect above.
    const postPlan = state.planChoice !== null || state.checkoutPending || checkoutReturn !== null;
    if (userData?.onboardingComplete) {
      if (postPlan) return;
      leavingRef.current = true;
      clearDraft();
      navigate("/download", { replace: true });
      return;
    }

    if (userData && !userData.onboardingComplete) {
      // Profile exists but onboarding never finished: a paid plan that left
      // checkout, or a legacy flow-B account.
      if (!state.existingProfile) {
        const resumeStep: OnboardingStep =
          userData.onboardingResumeStep === "plan" && stepIndex(state.step) < stepIndex("plan")
            ? "plan"
            : stepIndex(state.step) > stepIndex("competitionLevel")
              ? state.step
              : "competitionLevel";
        const level = COMPETITION_LEVELS.find((l) => l === userData.competitionLevel) ?? null;
        dispatch({
          type: "restore",
          draft: {
            existingProfile: true,
            accountReady: true,
            checkoutPending: checkoutReturn !== null && state.checkoutPending,
            emailVerifiedByProvider: Boolean(userData.isEmailVerified),
            email: currentUser.email ?? userData.email ?? "",
            firstName: state.firstName || userData.name || "",
            username: state.username || userData.username || "",
            dob: userData.dob || state.dob,
            sex: (userData.gender === "female" || userData.gender === "male" ? userData.gender : state.sex),
            selectedSportIDs: state.selectedSportIDs.length ? state.selectedSportIDs : userData.trainingSportIDs ?? [],
            competitionLevel: state.competitionLevel ?? (level as CompetitionLevel | null),
            successLook: state.successLook || userData.performanceGoals || "",
            step: resumeStep,
          },
        });
        if (userData.username) dispatch({ type: "setUsernameStatus", status: { kind: "available" } });
      }
      return;
    }

    // Auth user with no users/{uid} doc yet: resume at the ambassador step.
    if (!state.accountReady && authState.error === "User data not found") {
      const providerVouched = currentUser.providerData.some((p) => p.providerId !== "password");
      dispatch({
        type: "accountCreated",
        email: currentUser.email ?? "",
        firstName: (currentUser.displayName ?? "").split(" ")[0],
        emailVerifiedByProvider: providerVouched,
        next: stepIndex(state.step) > stepIndex("ambassadorCode") ? state.step : "ambassadorCode",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializing, currentUser, userData, authState.error, pendingTwoFactor, state.accountReady, state.existingProfile, state.submitting, state.planChoice, state.checkoutPending, checkoutReturn]);

  // ── Auth actions ─────────────────────────────────────────────────────────────

  const createAccount = async () => {
    if (!state.legalAccepted) return;
    if (state.accountReady) {
      dispatch({ type: "goTo", step: "ambassadorCode" });
      return;
    }
    const email = state.email.trim();
    const validation = emailError(email) ?? passwordError(state.password);
    if (validation) {
      dispatch({ type: "authError", message: validation });
      return;
    }
    if (state.authBusy) return;
    dispatch({ type: "authStart" });
    try {
      await createAccountWithEmail(email, state.password);
      trackCompleteRegistration();
      dispatch({ type: "accountCreated", email, emailVerifiedByProvider: false, next: "ambassadorCode" });
    } catch (error) {
      const code = (error as { code?: string }).code;
      const message =
        code === "auth/email-already-in-use"
          ? "An account with this email already exists. Download the app to continue."
          : getAuthErrorMessage(error);
      dispatch({ type: "authError", message });
    }
  };

  const continueWithProvider = async (method: "apple" | "google") => {
    if (!state.legalAccepted || state.authBusy) return;
    dispatch({ type: "authStart" });
    try {
      const raw = method === "apple" ? await continueWithApple() : await continueWithGoogle();
      if (!raw) {
        // 2FA challenge raised for an existing account — Login owns that UI.
        dispatch({ type: "authError", message: null });
        leavingRef.current = true;
        navigate("/download?existing=1", { replace: true });
        return;
      }
      const result = "isNewUser" in raw ? raw : describeProviderResult(raw);
      const existing = await userService.getUserData(result.user.uid).catch(() => null);
      if (existing) {
        // Existing Peak profile — this is a sign-in. The auth listener +
        // PrivateRoute route them (dashboard, or back here if incomplete).
        dispatch({ type: "authError", message: null });
        if (existing.onboardingComplete) {
          leavingRef.current = true;
          clearDraft();
          navigate("/download?existing=1", { replace: true });
        }
        return;
      }
      trackCompleteRegistration();
      dispatch({
        type: "accountCreated",
        email: result.user.email ?? "",
        firstName: result.firstName,
        emailVerifiedByProvider: true,
        next: "ambassadorCode",
      });
    } catch (error) {
      dispatch({ type: "authError", message: isPopupCancelled(error) ? null : getAuthErrorMessage(error) });
    }
  };

  const applyAmbassadorCode = () => {
    if (state.ambassadorValidated) {
      dispatch({ type: "continue" });
      return;
    }
    if (!ambassadorChecking) void checkAmbassadorCode(state.ambassadorCode);
  };

  // ── Plan ─────────────────────────────────────────────────────────────────────

  const registrationInput = (uid: string): OnboardingRegistrationInput => ({
    uid,
    email: currentUser?.email ?? state.email,
    firstName: state.firstName,
    username: state.username,
    dob: state.dob,
    sex: state.sex,
    selectedSportIDs: state.selectedSportIDs,
    successLook: state.successLook,
    competitionLevel: state.competitionLevel,
    campaignCode: state.ambassadorValidated ? state.ambassadorCode : "",
    existingProfile: state.existingProfile,
  });

  const choosePlan = async (plan: PlanChoice) => {
    if (state.submitting) return;
    const uid = currentUser?.uid;
    if (!uid) {
      setPlanError("Your session expired. Start signup again, or download the app if you already have an account.");
      dispatch({ type: "goTo", step: "createAccount" });
      return;
    }
    setPlanError(null);
    const onStatus = (status: string) => dispatch({ type: "submitStatus", status });
    dispatch({ type: "submitStart", status: FINISH_STATUS.profile });

    try {
      if (plan.type === "free") {
        await completeOnboarding(registrationInput(uid), { onStatus });
        analyticsService.trackProfileComplete(uid);
        analyticsService.trackOnboarding("complete", true);
        refreshUserData().catch(() => {});
        exitToDownload("/download?welcome=1");
        return;
      }

      // Saved first so the checkout webhook can credit the ambassador;
      // onboarding completes when checkout succeeds.
      await saveOnboardingProfile(registrationInput(uid), { complete: false, onStatus });
      onStatus(FINISH_STATUS.checkout);
      const fresh = await userService.getUserData(uid).catch(() => null);
      const checkoutUrl = await createPlanCheckoutUrl({
        uid,
        email: currentUser?.email ?? state.email,
        plan: { type: plan.type, name: plan.name, billingPeriod: plan.billingPeriod as "month" | "quarter" | "year" },
        validatedCountry: plan.validatedCountry,
        stripeCustomerId: fresh?.stripeCustomerId ?? userData?.stripeCustomerId ?? null,
        hasUsedTrial: fresh?.hasUsedTrial ?? userData?.hasUsedTrial,
        origin: window.location.origin,
      });
      if (checkoutUrl) {
        leavingRef.current = true;
        saveDraftNow(state, { step: "plan", existingProfile: true, checkoutPending: true });
        trackInitiateCheckout({
          type: plan.type,
          name: plan.name,
          billingPeriod: plan.billingPeriod === "year" ? "year" : "month",
        });
        // Stripe Checkout is an external origin — a hard navigation is required.
        window.location.href = checkoutUrl;
        return;
      }
      dispatch({ type: "restore", draft: { existingProfile: true } });
      dispatch({ type: "submitDone" });
      setPlanError("This plan is not available yet. Start on the free plan and upgrade any time from Settings.");
    } catch (error) {
      const message = error instanceof Error ? getAuthErrorMessage(error) : "Something went wrong finishing setup. Please try again.";
      dispatch({ type: "submitError", message });
      setPlanError(message);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (initializing) return <FullScreenLoader isLoading />;
  if (state.submitting) return <OnboardingFinishLoading status={state.submitStatus} />;
  if (checkoutReturn) return <FullScreenLoader isLoading />;

  const set = (field: "email" | "password" | "firstName" | "successLook" | "dob") => (value: string) =>
    dispatch({ type: "setField", field, value });

  switch (state.step) {
    case "welcome":
      return <WelcomeStep onContinue={continueTapped} onExistingAccount={() => navigate("/download?existing=1")} />;
    case "createAccount":
      return (
        <CreateAccountStep
          authBusy={state.authBusy}
          authError={state.authError}
          legalAccepted={state.legalAccepted}
          onLegalAcceptedChange={(accepted) => dispatch({ type: "setLegalAccepted", accepted })}
          onBack={goBack}
          onApple={() => continueWithProvider("apple")}
          onGoogle={() => continueWithProvider("google")}
          onEmail={() => dispatch({ type: "openEmail" })}
          onExistingAccount={() => navigate("/download?existing=1")}
        />
      );
    case "email":
      return (
        <EmailStep
          email={state.email}
          password={state.password}
          authBusy={state.authBusy}
          authError={state.authError}
          canContinue={canContinue("email")}
          legalAccepted={state.legalAccepted}
          onLegalAcceptedChange={(accepted) => dispatch({ type: "setLegalAccepted", accepted })}
          onEmailChange={set("email")}
          onPasswordChange={set("password")}
          onBack={goBack}
          onCreateAccount={createAccount}
        />
      );
    case "ambassadorCode":
      return (
        <AmbassadorCodeStep
          code={state.ambassadorCode}
          ambassadorFirstName={state.ambassadorValidated ? state.ambassadorFirstName : null}
          canContinue={canContinue("ambassadorCode")}
          checking={ambassadorChecking}
          error={ambassadorError}
          onChange={(value) => {
            setAmbassadorError(null);
            dispatch({ type: "setField", field: "ambassadorCode", value });
          }}
          onBack={goBack}
          onApply={applyAmbassadorCode}
          onSkip={() => dispatch({ type: "skipAmbassadorCode" })}
        />
      );
    case "competitionLevel":
      return (
        <CompetitionLevelStep
          value={state.competitionLevel}
          progress={progress}
          showBack={!state.existingProfile}
          onSelect={(value) => dispatch({ type: "selectCompetitionLevel", value })}
          onBack={goBack}
          onContinue={continueTapped}
        />
      );
    case "sports":
      return (
        <SportsStep
          sports={state.availableSports}
          selectedIDs={state.selectedSportIDs}
          loading={state.sportsLoading}
          loadError={state.sportsLoadError}
          progress={progress}
          onToggle={(id) => dispatch({ type: "toggleSport", id })}
          onBack={goBack}
          onContinue={continueTapped}
        />
      );
    case "sexAndDob":
      return (
        <PersonalInfoStep
          firstName={state.firstName}
          username={state.username}
          usernameStatus={state.usernameStatus}
          sex={state.sex}
          dob={state.dob}
          progress={progress}
          canContinue={canContinue("sexAndDob")}
          onFirstNameChange={set("firstName")}
          onUsernameChange={setUsername}
          onSexChange={(value) => dispatch({ type: "selectSex", value })}
          onDobChange={set("dob")}
          onBack={goBack}
          onContinue={continueTapped}
        />
      );
    case "success":
      return (
        <GoalStep
          value={state.successLook}
          progress={progress}
          canContinue={canContinue("success")}
          onChange={set("successLook")}
          onBack={goBack}
          onContinue={continueTapped}
          onSkip={() => dispatch({ type: "skipGoal" })}
        />
      );
    case "difference":
      return <DifferenceStep progress={progress} onBack={goBack} onContinue={continueTapped} />;
    case "plan":
      return (
        <PlanStep
          progress={progress}
          loading={state.submitting}
          error={planError ?? state.submitError}
          onBack={goBack}
          onSelect={choosePlan}
        />
      );
    case "day1":
    case "howPeakHelps":
      return <FullScreenLoader isLoading />;
    default:
      return null;
  }
};

export default OnboardingWizard;
